import { LegacyRef, useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  DatePicker,
  Input,
  Modal,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Popover,
  PopoverAction,
  PopoverContent,
  Spinner,
  Switch,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
  Tabs,
  TabsContent,
  TabsItem,
  TabsList,
  toast,
  Upload,
  UploadBody,
  UploadText,
} from 'keep-react'

import { Wrapper } from 'components'
import Quill from 'quill'
import { Editor } from 'components/QuillEditor'
import { ArrowLeft, Calendar, FloppyDisk, Trash } from 'phosphor-react'
import { RouteNamesAuth } from 'routes/enum'
import { CampaignType, SaveCampaignBody } from 'api/types/campaign'
import { campaignService } from 'api/services/campaign'
import { format } from 'date-fns'

// form validation schema
const validationSchema = Yup.object().shape({
  categoryId: Yup.number(),
  status: Yup.number().oneOf([0, 1]),
  hasSimulation: Yup.boolean(),
  campaignFiles: Yup.mixed().nullable(),
})

export const BackOfficeCampaign = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  //first load and is api being called
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isApiBeingCalled, setIsApiBeingCalled] = useState<boolean>(false)

  //loaded campaign data
  const [data, setData] = useState<CampaignType | null>(null)

  // Quill States
  const [content, setContent] = useState<string>('')

  // Modal Control
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)

  // Save Button Active?
  const [hasChanges, setHasChanges] = useState<boolean>(false)

  // Files to upload
  const [file, setFile] = useState<File | null>(null)

  // Delete File Modal
  const [showDeleteFileModal, setShowDeleteFileModal] = useState<boolean>(false)

  // File to delete
  const [fileToDelete, setFileToDelete] = useState<string | null>(null)

  // Editor Ref
  const quillContentRef = useRef(null)

  //get campaign details
  const getCampaign = async () => {
    setIsLoading(true)
    try {
      const response = (await campaignService.getCampaign({
        id: id as string,
      })) as CampaignType
      setData(response as CampaignType)
      formik.setFieldValue('id', response.id)
      formik.setFieldValue('title', response.title)
      setContent(response.content)
      formik.setFieldValue('description', response.description)
      formik.setFieldValue('label', response.label)
      formik.setFieldValue('link', response.link)
      formik.setFieldValue('createdDate', response.createdDate)
      formik.setFieldValue('expirationDate', response.expirationDate)
      formik.setFieldValue('active', response.active)

      setIsLoading(false)
    } catch (error) {
      toast.error('Não foi possível carregar as campanhas.')
    }
  }

  // using formik to handle form state
  const formik = useFormik({
    initialValues: {
      id: id,
      title: '',
      description: '',
      label: '',
      link: '',
      createdDate: '',
      expirationDate: '',
      active: false,
    },
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema,
    onSubmit: async values => {
      if (!id) return
      const newValues = {
        id: values.id,
        title: values.title,
        description: values.description,
        content: content,
        label: values.label,
        link: values.link,
        createdDate: format(
          values.createdDate as unknown as Date,
          'yyyy-MM-dd',
        ),
        expirationDate: format(
          values.expirationDate as unknown as Date,
          'yyyy-MM-dd',
        ),
        active: values.active,
      } as SaveCampaignBody

      try {
        setIsApiBeingCalled(true)
        await campaignService.saveCampaign(newValues)

        toast.success('Campanha atualizada com sucesso.')
        setHasChanges(false)
      } catch (error) {
        toast.error('Erro ao atualizar campanha.')
      } finally {
        setIsApiBeingCalled(false)
      }
    },
  })

  // formik for file upload
  const uploadFormik = useFormik({
    initialValues: {
      label: '',
      file: null,
    },
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async () => {
      if (!data || !file) return
      try {
        setIsApiBeingCalled(true)
        await campaignService.addPortalCampaignFiles({
          id: String(data.id),
          file: {
            label: uploadFormik.values.label,
            file: file,
          },
        })
        setFile(null)
        uploadFormik.setFieldValue('label', '')
        // refresh campaign data
        getCampaign()
        toast.success('Ficheiro(s) carregado(s) com sucesso.')
      } catch (error) {
        toast.error('Erro ao carregar ficheiro(s).')
      } finally {
        setIsApiBeingCalled(false)
      }
    },
  })

  // delete selected campaign
  const deleteCampaign = async () => {
    if (!data) return
    try {
      await campaignService.deleteCampaign({
        id: String(data.id),
      })
      setShowDeleteModal(false)
      toast.success('Campanha apagada com sucesso.')
      navigate(RouteNamesAuth.BACKOFFICE_CAMPAIGNS)
    } catch (error) {
      toast.error('Não foi possível apagar a campanha.')
    }
  }

  // delete selected file
  const deleteFile = async () => {
    if (!data || !fileToDelete) return
    try {
      setIsApiBeingCalled(true)
      await campaignService.removePortalCampaignFile({
        campaignid: String(data.id),
        fileId: fileToDelete,
      })
      setShowDeleteFileModal(false)
      toast.success('Ficheiro apagado com sucesso.')
      // refresh campaign data
      getCampaign()
    } catch (error) {
      toast.error('Não foi possível apagar o ficheiro.')
    } finally {
      setIsApiBeingCalled(false)
    }
  }

  const onDrop = useCallback(<T extends File>(acceptedFiles: T[]) => {
    setFile(acceptedFiles && acceptedFiles.length > 0 ? acceptedFiles[0] : null)
  }, [])

  // initial data fetch
  useEffect(() => {
    getCampaign()
  }, [])

  // track changes to enable save button
  useEffect(() => {
    setHasChanges(true)
  }, [content, formik.values])

  return (
    <div className="w-full flex-1 py-12 overflow-auto">
      <Wrapper>
        {isLoading || !data ? (
          <Card className="w-full max-w-full p-4 md:p-6">
            <Spinner color="primary" className="mx-auto" />
          </Card>
        ) : (
          <>
            <Button
              variant="outline"
              color="secondary"
              className="mb-6"
              onClick={() => navigate(RouteNamesAuth.BACKOFFICE_CAMPAIGNS)}
            >
              <ArrowLeft size={16} /> Campanhas
            </Button>
            <Card className="w-full max-w-full p-4 md:p-6">
              <h1 className="pb-10 text-xxxl font-medium text-metal-900 flex items-center justify-between">
                <span></span>
                <>
                  <ButtonGroup>
                    <Button
                      type="submit"
                      position="start"
                      color="secondary"
                      variant="default"
                      disabled={!hasChanges || isApiBeingCalled}
                      className="gap-2"
                      onClick={() => {
                        formik.handleSubmit()
                      }}
                    >
                      <FloppyDisk size={16} />
                      Gravar alterações
                    </Button>

                    <Button
                      position="end"
                      variant="default"
                      color="error"
                      onClick={() => {
                        setShowDeleteModal(true)
                      }}
                      disabled={isApiBeingCalled}
                      className="gap-2"
                    >
                      <Trash size={16} />
                      Apagar Campanha
                    </Button>
                  </ButtonGroup>
                </>
              </h1>

              <Tabs defaultValue="general">
                <TabsList>
                  <TabsItem value="general">Geral</TabsItem>
                  <TabsItem value="documents">Documentos</TabsItem>
                </TabsList>
                <TabsContent value="general" className="p-4 md:p-6">
                  <div className="space-y-8">
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Nome
                      </h3>
                      <Input
                        name="title"
                        value={formik.values.title}
                        onChange={formik.handleChange}
                      />
                    </div>
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Descrição / Subtítulo
                      </h3>
                      <Input
                        name="description"
                        value={formik.values.description}
                        onChange={formik.handleChange}
                      />
                    </div>
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Conteúdo
                      </h3>
                      <div>
                        <Editor
                          ref={
                            quillContentRef as unknown as
                              | LegacyRef<Quill>
                              | undefined
                          }
                          defaultValue={content}
                          setContent={setContent}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Link
                      </h3>
                      <Input
                        name="link"
                        value={formik.values.link}
                        onChange={formik.handleChange}
                      />
                    </div>
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Label do botão
                      </h3>
                      <Input
                        name="label"
                        value={formik.values.label}
                        onChange={formik.handleChange}
                      />
                    </div>
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Data de criação
                      </h3>
                      <Popover>
                        <PopoverAction asChild>
                          <Button
                            color="secondary"
                            size="lg"
                            className="w-[286px] justify-start gap-2 border border-metal-100 text-body-4 text-metal-900"
                            variant="outline"
                          >
                            <Calendar
                              size={20}
                              className="text-metal-400 dark:text-white"
                            />
                            {formik.values.createdDate ? (
                              format(
                                new Date(formik.values.createdDate),
                                'dd/MM/yyyy',
                              )
                            ) : (
                              <span>Selecione a data de criação</span>
                            )}
                          </Button>
                        </PopoverAction>
                        <PopoverContent
                          align="start"
                          className="max-w-min border-0"
                        >
                          <DatePicker
                            dayShape="circle"
                            mode="single"
                            selected={
                              formik.values.createdDate as unknown as Date
                            }
                            onSelect={value =>
                              formik.setFieldValue('createdDate', value)
                            }
                            showOutsideDays={true}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Data de término
                      </h3>
                      <Popover>
                        <PopoverAction asChild>
                          <Button
                            color="secondary"
                            size="lg"
                            className="w-[286px] justify-start gap-2 border border-metal-100 text-body-4 text-metal-900"
                            variant="outline"
                          >
                            <Calendar
                              size={20}
                              className="text-metal-400 dark:text-white"
                            />
                            {formik.values.expirationDate ? (
                              format(
                                new Date(formik.values.expirationDate),
                                'dd/MM/yyyy',
                              )
                            ) : (
                              <span>Selecione a data de término</span>
                            )}
                          </Button>
                        </PopoverAction>
                        <PopoverContent
                          align="start"
                          className="max-w-min border-0"
                        >
                          <DatePicker
                            dayShape="circle"
                            mode="single"
                            selected={
                              formik.values.expirationDate as unknown as Date
                            }
                            onSelect={value =>
                              formik.setFieldValue('expirationDate', value)
                            }
                            showOutsideDays={true}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Status
                      </h3>
                      <span className="flex items-center gap-2">
                        {data.active ? (
                          <Badge
                            className="pointer-events-none"
                            color="success"
                            variant="base"
                          >
                            Ativo
                          </Badge>
                        ) : (
                          <Badge
                            className="pointer-events-none"
                            color="error"
                            variant="base"
                          >
                            Inativo
                          </Badge>
                        )}
                        <Switch
                          checked={formik.values.active}
                          onCheckedChange={() => {
                            setHasChanges(true)
                            formik.setFieldValue(
                              'active',
                              !formik.values.active,
                            )
                          }}
                          className="data-[state=checked]:bg-metal-500 text-metal-500 [&_span]:data-[state=checked]:before:bg-metal-500"
                        />
                      </span>
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="documents" className="p-4 md:p-6">
                  <>
                    {data.files && data.files.length === 0 && (
                      <p className="text-body-4">
                        Não há documentos disponíveis para esta campanha.
                      </p>
                    )}

                    {data.files && data.files.length > 0 && (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Label</TableHead>
                            <TableHead>Ficheiro</TableHead>
                            <TableHead>Tipo de Conteúdo</TableHead>
                            <TableHead>Tamanho (bytes)</TableHead>
                            <TableHead></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {data.files.length > 0 &&
                            data.files.map(file => (
                              <TableRow key={file.id}>
                                <TableCell>{file.label}</TableCell>
                                <TableCell>
                                  <Button variant="link" color="primary">
                                    <Link to={file.uri} target="_blank">
                                      Ver Ficheiro
                                    </Link>
                                  </Button>
                                </TableCell>
                                <TableCell>{file.contentType}</TableCell>
                                <TableCell>{file.sizeBytes}</TableCell>
                                <TableCell>
                                  <Button
                                    variant="outline"
                                    color="error"
                                    className="gap-2"
                                    onClick={() => {
                                      setFileToDelete(file.id.toString())
                                      setShowDeleteFileModal(true)
                                    }}
                                  >
                                    <Trash size={16} />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    )}

                    <div className="pt-6">
                      <h3 className="text-xl text-primary-500 pb-4">
                        Adicionar documentos
                      </h3>
                      <form className="flex items-start gap-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                          <div>
                            <h3 className="text-body-4 font-bold text-metal-900 pb-2">
                              Label
                            </h3>
                            <Input
                              type="text"
                              name="label"
                              onChange={e =>
                                uploadFormik.setFieldValue(
                                  'label',
                                  e.currentTarget.value,
                                )
                              }
                            />
                          </div>
                          <div>
                            <h3 className="text-body-4 font-bold text-metal-900 pb-2">
                              Ficheiro
                            </h3>
                            <Upload
                              horizontal
                              options={{ onDrop, multiple: false }}
                              className="p-0 border-none"
                            >
                              {file ? (
                                <p className="text-body-4 font-semibold text-metal-600 flex items-center justify-between h-11">
                                  <span>{file.name}</span>
                                  <Button
                                    color="error"
                                    variant="link"
                                    onClick={() => setFile(null)}
                                  >
                                    <Trash size={16} />
                                  </Button>
                                </p>
                              ) : (
                                <UploadBody>
                                  <UploadText>
                                    <p className="text-body-4 font-medium text-metal-600">
                                      Escolha ou arraste o seu ficheiro aqui
                                      (PDF, JPG ou WEBP))
                                    </p>
                                  </UploadText>
                                </UploadBody>
                              )}
                            </Upload>
                          </div>
                        </div>
                        <div className="col-span-2 justify-self-end pt-8">
                          <Button
                            disabled={!file || !uploadFormik.values.label}
                            type="button"
                            onClick={() => uploadFormik.handleSubmit()}
                          >
                            Carregar Ficheiro
                          </Button>
                        </div>
                      </form>
                    </div>
                  </>
                </TabsContent>
              </Tabs>
            </Card>
          </>
        )}
      </Wrapper>

      {/* Delete Campaign Modal */}
      <Modal open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <ModalContent>
          <ModalHeader className="mb-6 space-y-3">
            <ModalTitle>Apagar Campanha</ModalTitle>
            <ModalDescription>
              Quer apagar esta campanha?
              <br />
              <br />
              <strong className="text-error-500">
                Todos os dados desta campanha irão ser perdidos.
              </strong>
            </ModalDescription>
          </ModalHeader>
          <ModalFooter className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              color="secondary"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit" onClick={() => deleteCampaign()}>
              Apagar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete File Modal */}
      <Modal open={showDeleteFileModal} onOpenChange={setShowDeleteFileModal}>
        <ModalContent>
          <ModalHeader className="mb-6 space-y-3">
            <ModalTitle>Apagar Ficheiro</ModalTitle>
            <ModalDescription>
              Quer apagar este ficheiro?
              <br />
              <br />
              <strong className="text-error-500">
                Este ficheiro irá ser apagado.
              </strong>
            </ModalDescription>
          </ModalHeader>
          <ModalFooter className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              color="secondary"
              onClick={() => setShowDeleteFileModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit" onClick={() => deleteFile()}>
              Apagar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}
